import { action, computed, observable } from "mobx";
import { THEME_SETTINGS } from "@theme-settings";
import { WaitingPeriodPopupStateEnum } from "@utils/common/waitingPeriodPopupStateEnum";
import { WaitingPeriodPopupTypeEnum } from "@utils/common/waitingPeriodPopupTypeEnum";
import { redirect } from "@state/utils";
import { getCurrentCulture} from "@utils";
import { StorageStateKeysEnum } from "@utils/storage";
import { PlayTypeEnum } from "@data-types/games/casino/PlayTypeEnum";
import type { RootStore } from "@state/stores/types";

class WaitingPeriodStore {
	@observable popupType: WaitingPeriodPopupTypeEnum | null = null;
	@observable popupState: WaitingPeriodPopupStateEnum =
		WaitingPeriodPopupStateEnum.HIDDEN;
	@observable timeLeft: number = 0;

	intervalId: NodeJS.Timeout | null = null;

	readonly SIXTY_SECONDS: number = 60 * 1000;
	readonly THIRTY_SECONDS: number = 30 * 1000;
	readonly FIVE_MINUTES: number = 5 * 60 * 1000;

	rootStore: RootStore;

	constructor(rootStore: RootStore) {
		this.rootStore = rootStore;
		this.restorePopupState();
	}

	onInitialize() {
		const storedPopupValues = this.getWaitingPeriodState();

		const casinoPlayType = this.rootStore.localStorageProvider.get(
			StorageStateKeysEnum.CASINO_PLAY_TYPE
		);

		if (
			!THEME_SETTINGS.isWaitingPeriodEnabled ||
			this.popupState == WaitingPeriodPopupStateEnum.HIDDEN ||
			Object.keys(storedPopupValues).length == 0
		) {
			return;
		}

		this.popupState = storedPopupValues.popupState;

		if (this.hasFirstTimerExpired()) {
			delete storedPopupValues.firstTimerPassedTimestamp;
		}

		const shouldSkipTimerUpdates =
			(this.popupType === WaitingPeriodPopupTypeEnum.GAME ||
				this.popupType === null) &&
			casinoPlayType === PlayTypeEnum.FUN;

		let timerDuration = storedPopupValues.timerDuration;

		if (!shouldSkipTimerUpdates) {
			if (!timerDuration) {
				timerDuration = this.hasFirstTimerExpired()
					? this.SIXTY_SECONDS
					: this.THIRTY_SECONDS;

				this.updateWaitingPeriodState({
					timerDuration,
				});
			}
		}

		let endTime = storedPopupValues.endTime;

		if (!shouldSkipTimerUpdates) {
			if (!endTime) {
				endTime = Date.now() + (timerDuration || 0);

				const updates: Record<string, any> = {
					endTime,
				};

				if (this.hasFirstTimerExpired()) {
					updates.firstTimerPassedTimestamp = Date.now();
				}

				this.updateWaitingPeriodState(updates);
			}
		}

		if (this.popupType) {
			this.startTimer(
				this.popupType,
				parseInt(endTime || storedPopupValues.endTime, 10)
			);
		}
	}

	hidePopupOnEmptySlip() {
		if (this.rootStore.betSlipStore.isSlipEmpty) {
			this.hidePopup();
		}
	}

	hasFirstTimerExpired(): boolean {
		const { firstTimerPassedTimestamp } = this.getWaitingPeriodState();

		if (!firstTimerPassedTimestamp) return true;

		return (
			Date.now() - Number(firstTimerPassedTimestamp) > this.FIVE_MINUTES
		);
	}

	startTimer(popupType: WaitingPeriodPopupTypeEnum, endTime: number) {
		if (this.intervalId) {
			clearInterval(this.intervalId);
			this.intervalId = null;
		}

		this.popupType = popupType;
		this.updateTimeLeft(endTime);

		this.intervalId = setInterval(() => {
			const timeDiff = endTime - Date.now();

			if (timeDiff <= 0) {
				this.timeLeft = 0;
				clearInterval(this.intervalId!);
				this.intervalId = null;

				if (!this.rootStore.betSlipStore?.isOneClickBetActive) {
					this.intervalId = setTimeout(() => {
						this.hidePopup();
						this.intervalId = null;
					}, 5000);
				} else {
					this.hidePopup();
				}
			} else {
				this.timeLeft = timeDiff;
			}
		}, 1000);
	}

	updateTimeLeft(endTime: number) {
		const timeDiff = endTime - Date.now();
		this.timeLeft = timeDiff > 0 ? timeDiff : 0;
	}

	@computed
	get getFormattedTime() {
		return String(Math.ceil(this.timeLeft / 1000)).padStart(2, "0");
	}

	storeGameUrl() {
		const currentUrl = window.location.pathname;

		this.updateWaitingPeriodState({
			gameUrl: currentUrl.slice(3),
		});
	}

	@action.bound
	async submitSlip() {
		this.hidePopup();
		await this.rootStore.betSlipStore?.onSubmitSlip();
	}

	@action.bound
	resetWaitingPeriodDuration() {
		this.onDispose();
	}

	@action.bound
	async redirectToGame() {
		const storedPopupValues = this.getWaitingPeriodState();
		const storedGameUrl = storedPopupValues.gameUrl as string;

		if (storedGameUrl) {
			redirect(`/${getCurrentCulture()}${storedGameUrl}`);
		}

		this.hidePopup();

		await this.rootStore.retryHandlerStore.executeRetry();
	}

	@action.bound
	restorePopupState() {
		const storedPopupValues = this.getWaitingPeriodState();

		if (Object.keys(storedPopupValues).length == 0) {
			return;
		}

		this.popupState = storedPopupValues.popupState;

		this.popupType = storedPopupValues.popupType ?? null;

		this.timeLeft = storedPopupValues.endTime
			? storedPopupValues.endTime - Date.now()
			: 0;

		if (this.popupType) {
			this.startTimer(
				this.popupType,
				parseInt(storedPopupValues.endTime, 10)
			);
		}
	}

	@action.bound
	showPopup(popupType: WaitingPeriodPopupTypeEnum) {
		this.popupState = WaitingPeriodPopupStateEnum.ACTIVE;
		this.popupType = popupType;

		this.updateWaitingPeriodState({
			popupState: this.popupState,
			popupType: this.popupType,
		});

		this.onInitialize();
	}

	@action.bound
	hidePopup() {
		this.popupState = WaitingPeriodPopupStateEnum.HIDDEN;

		this.removePopupUiStateFromStorage();
	}

	//activate and deactivate used for one click bet handling
	@action.bound
	deactivatePopup() {
		this.popupState = WaitingPeriodPopupStateEnum.HIDDEN;

		this.updateWaitingPeriodState({
			popupState: this.popupState,
		});
	}

	@action.bound
	activatePopup() {
		if (
			this.rootStore.betSlipStore.isSlipEmpty &&
			!this.rootStore.betSlipStore.isOneClickBetActive
		) {
			return;
		}

		if (this.timeLeft > 0) {
			if (this.rootStore.betSlipStore.isOneClickBetActive) {
				this.popupState = WaitingPeriodPopupStateEnum.ACTIVE;
			} else {
				this.popupState = WaitingPeriodPopupStateEnum.MINIMIZED;
			}

			this.updateWaitingPeriodState({
				popupState: this.popupState,
			});
		}
	}

	@action.bound
	minimizePopup() {
		this.popupState = WaitingPeriodPopupStateEnum.MINIMIZED;

		this.updateWaitingPeriodState({
			popupState: this.popupState,
		});
	}

	@action.bound
	onDispose() {
		this.popupState = WaitingPeriodPopupStateEnum.HIDDEN;
		this.timeLeft = 0;

		if (this.intervalId) {
			clearInterval(this.intervalId);
			this.intervalId = null;
		}

		this.removePopupDataFromLocalStorage();
	}

	setWaitingPeriodState(newState: Record<string, any>) {
		this.rootStore.localStorageProvider.set(
			StorageStateKeysEnum.WAITING_PERIOD_STATE,
			JSON.stringify(newState)
		);
	}

	getWaitingPeriodState(): Record<string, any> {
		try {
			const rawData = this.rootStore.localStorageProvider.get(
				StorageStateKeysEnum.WAITING_PERIOD_STATE
			) as string;

			const parsed = rawData ? JSON.parse(rawData) : null;

			return parsed && typeof parsed === "object" ? parsed : {};
		} catch (e) {
			console.error("Failed to parse waiting period state", e);
			return {};
		}
	}

	updateWaitingPeriodState(updates: Record<string, any>) {
		const currentPopupValues = this.getWaitingPeriodState();

		const newPopupValues = { ...currentPopupValues, ...updates };

		this.rootStore.localStorageProvider.set(
			StorageStateKeysEnum.WAITING_PERIOD_STATE,
			JSON.stringify(newPopupValues)
		);
	}

	removePopupDataFromLocalStorage() {
		this.rootStore.localStorageProvider.remove(
			StorageStateKeysEnum.WAITING_PERIOD_STATE
		);
	}

	removePopupUiStateFromStorage() {
		let storedPopupValues = this.getWaitingPeriodState();

		delete storedPopupValues.popupState;
		delete storedPopupValues.popupType;
		delete storedPopupValues.endTime;
		delete storedPopupValues.timerDuration;

		if (storedPopupValues.gameUrl) {
			delete storedPopupValues.gameUrl;
		}

		this.setWaitingPeriodState(storedPopupValues);

		this.timeLeft = 0;

		if (this.intervalId) {
			clearInterval(this.intervalId);
			this.intervalId = null;
		}
	}
}

export default WaitingPeriodStore;
